import { type FC } from "react"
import { getExtraDataDisplayName, ExtraDataTable } from "~/utils/dynamicTableUtils"

import Select from "~/components/reusable/Select"
import { useCompany } from "~/hooks/useCompany"
import { useExtraDataDisplayNameMap } from "~/hooks/useSku"
import { replaceName } from "~/utils/namingUtils"

// https://maxheiber.medium.com/alternatives-to-typescript-enums-50e4c16600b1
export type AggregationMethod = (typeof AggregationMethod)[keyof typeof AggregationMethod]

export const AggregationMethod = {
  ProductName: "Product Name",
  Variant: "Variant",
  SKU: "SKU",
  Brand: "Brand",
  Category: "Category",
  Gender: "Gender",
  Color: "Color",
  Size: "Size",
  Vendor: "Vendor",
  PreferredVendor: "Preferred Vendor",
  PurchaseOrder: "Purchase Order",
  ProductionOrder: "Production Order",
  Warehouse: "Warehouse",
  SkuWarehouse: "SKU Warehouse",
  LeadTime: "Lead Time",
  PurchaseInterval: "Purchase Interval",
  ABC: "ABC",
  Collection: "Collection",
  Collections: "Collections", // Used for CollectionOverviewTab
  ProductType: "Product Type",
  Customer: "Customer",
  VariantId: "Variant Id",
  Country: "Country",
  City: "City",
  ZipCode: "Zip Code",
  Planner: "Planner",
  CustomerType: "Customer Type",
  ForecastModel: "Forecast Model",
  Fabric: "Fabric",
  ExpectedArrivalDate: "Expected Arrival Date",
  All: "All",
  ShipmentMethod: "Shipment Method",
  Status: "Status",
} as const

export const defaultAggregations = [
  AggregationMethod.ProductName,
  AggregationMethod.Variant,
  AggregationMethod.Brand,
  AggregationMethod.Category,
  AggregationMethod.Gender,
  AggregationMethod.Color,
  AggregationMethod.Vendor,
  AggregationMethod.PreferredVendor,
  AggregationMethod.LeadTime,
  AggregationMethod.PurchaseInterval,
  AggregationMethod.ABC,
  AggregationMethod.Collection,
  AggregationMethod.ProductType,
  AggregationMethod.ForecastModel,
]

interface AggregationSelectorProps {
  selectedMethod: string
  onSelect: (method: AggregationMethod | string) => void
  aggregationMethods: AggregationMethod[]
  dynamicAggregationMethods?: string[]
  collectionDynamicAggregationMethods?: string[]
  incomingOrderAggregationMethods?: string[]
  title?: string
  prefix?: string
  className?: string
  menuAlign?: "left" | "right"
}

export const AggregationSelector: FC<AggregationSelectorProps> = ({
  selectedMethod,
  onSelect,
  aggregationMethods,
  dynamicAggregationMethods,
  collectionDynamicAggregationMethods,
  incomingOrderAggregationMethods,
  title,
  prefix = "Group by:",
  className,
  menuAlign,
}) => {
  const hasDynamicMethods =
    (dynamicAggregationMethods?.length ?? 0) > 0 ||
    (collectionDynamicAggregationMethods?.length ?? 0) > 0 ||
    (incomingOrderAggregationMethods?.length ?? 0) > 0
  const { data: extraDataDisplayNameMap } = useExtraDataDisplayNameMap(hasDynamicMethods)
  const { data: company } = useCompany()

  const options = aggregationMethods.map((key) => {
    const value = key.toString()
    const isCompanyDefault =
      !!company?.defaultAggregationLevel && value === company.defaultAggregationLevel
    return {
      label: replaceName(value) + (isCompanyDefault ? " (Company setting)" : ""),
      value,
    }
  })
  if (dynamicAggregationMethods?.length) {
    dynamicAggregationMethods.map((method) => {
      const keyName = method.replace("(extradata)", "")
      const label = getExtraDataDisplayName(keyName, ExtraDataTable.SKU, extraDataDisplayNameMap)
      if (method) {
        options.push({
          label: label + " (External)",
          value: method,
        })
      }
    })
  }
  if (collectionDynamicAggregationMethods?.length) {
    collectionDynamicAggregationMethods.map((method) => {
      const keyName = method.replace("(collection-extradata)", "")
      const label = getExtraDataDisplayName(
        keyName,
        ExtraDataTable.COLLECTION_ON_SKU,
        extraDataDisplayNameMap,
      )
      if (method) {
        options.push({
          label: label + " (Collection)",
          value: method,
        })
      }
    })
  }
  if (incomingOrderAggregationMethods?.length) {
    incomingOrderAggregationMethods.map((method) => {
      const keyName = method.replace("(incoming-order-extradata)", "")
      const label = getExtraDataDisplayName(
        keyName,
        ExtraDataTable.INCOMING_ORDER_ITEM,
        extraDataDisplayNameMap,
      )
      if (method) {
        options.push({
          label: label + " (External)",
          value: method,
        })
      }
    })
  }

  const handleSelect = (value: string) => {
    if (aggregationMethods.find((x) => x == (value as AggregationMethod))) {
      onSelect(value as AggregationMethod)
    } else {
      onSelect(value)
    }
  }

  return (
    <div className={`flex flex-row items-center gap-2 ${className}`}>
      {title && <div className="label-base text-fg-subtle">{title}</div>}
      <Select
        className="min-w-[120px]"
        value={options.find((option) => option.value === selectedMethod)}
        onChange={(e) => handleSelect(e?.value ?? AggregationMethod.ProductName)}
        options={options}
        isSearchable={false}
        size="sm"
        prefix={prefix?.length > 0 ? prefix : undefined}
        menuAlign={menuAlign}
      />
    </div>
  )
}
